/**
 * Submission Form Page
 *
 * Dynamic form that encrypts data before submission.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { getAuthorityPublicInfo, getFormDefinition } from '../api/citizenApi';
import { useSubmission } from '../hooks/useSubmission';
import { DynamicForm } from '../components/forms';

export function SubmissionFormPage() {
  const { authorityKeyId, processKey } = useParams<{
    authorityKeyId: string;
    processKey: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnChannelEnabled = (location.state as { returnChannelEnabled?: boolean } | null)?.returnChannelEnabled ?? true;
  const [encryptionStep, setEncryptionStep] = useState<
    'idle' | 'generating' | 'encrypting' | 'submitting'
  >('idle');

  const { submitFormV2, isSubmitting, error: submissionError, clearError } = useSubmission();

  // Fetch authority public key info (for V2 encryption)
  // Note: Uses different query key than ProcessLandingPage to avoid cache collision
  // (different endpoints return different data structures)
  const {
    data: authority,
    isLoading: isLoadingAuthority,
    error: authorityError,
  } = useQuery({
    queryKey: ['authority-public-key', authorityKeyId],
    queryFn: () => getAuthorityPublicInfo(authorityKeyId!),
    enabled: !!authorityKeyId,
    retry: 1, // Only retry once to fail fast for invalid IDs
  });

  // Fetch form definition using processKey
  const {
    data: formDef,
    isLoading: isLoadingForm,
    error: formError,
  } = useQuery({
    queryKey: ['formDefinition', processKey],
    queryFn: () => getFormDefinition(processKey!),
    enabled: !!processKey,
    retry: 1, // Only retry once to fail fast for invalid keys
  });

  // Clear error on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const onSubmit = async (data: Record<string, unknown>) => {
    if (!authority || !processKey) return;

    try {
      setEncryptionStep('generating');
      await new Promise((resolve) => setTimeout(resolve, 500)); // Brief pause for UX

      setEncryptionStep('encrypting');
      await new Promise((resolve) => setTimeout(resolve, 500)); // Brief pause for UX

      setEncryptionStep('submitting');
      // Use V2 encryption (authority-based with AEK)
      await submitFormV2(data, authority, processKey, { returnChannelEnabled });
    } catch (err) {
      console.error('Submission failed:', err);
      setEncryptionStep('idle');
    }
  };

  const handleBack = () => {
    navigate(`/submit/${authorityKeyId}/${processKey}`);
  };

  if (isLoadingAuthority || isLoadingForm) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {isLoadingAuthority ? 'Behörde wird geladen...' : 'Formular wird geladen...'}
        </Typography>
      </Box>
    );
  }

  if (authorityError || !authority) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          Behörde konnte nicht geladen werden. Bitte versuchen Sie es erneut.
        </Alert>
      </Box>
    );
  }

  if (formError || !formDef) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          Das Formular für diesen Prozess konnte nicht geladen werden.
          Möglicherweise ist der Prozess nicht korrekt konfiguriert.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/submit/${authorityKeyId}`)}
        >
          Zurück zur Prozessauswahl
        </Button>
      </Box>
    );
  }

  const isProcessing = encryptionStep !== 'idle';

  const getProcessingText = () => {
    switch (encryptionStep) {
      case 'generating':
        return 'Schlüssel wird generiert...';
      case 'encrypting':
        return 'Daten werden verschlüsselt...';
      case 'submitting':
        return 'Wird gesendet...';
      default:
        return 'Wird verarbeitet...';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 700, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ mr: 2 }}
              disabled={isProcessing}
            >
              Zurück
            </Button>
            <Box sx={{ flex: 1 }} />
            <Chip
              icon={<LockIcon />}
              label="Ende-zu-Ende verschlüsselt"
              color="success"
              size="small"
            />
          </Box>

          <Typography variant="h5" component="h1" gutterBottom>
            {formDef.title || 'Antrag einreichen'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Empfänger: {authority.authorityName}
          </Typography>

          {formDef.description && (
            <Typography variant="body2" color="text.secondary" paragraph>
              {formDef.description}
            </Typography>
          )}

          {/* Encryption Progress */}
          {isProcessing && (
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={['generating', 'encrypting', 'submitting'].indexOf(encryptionStep)}>
                <Step>
                  <StepLabel>Schlüssel generieren</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Daten verschlüsseln</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Senden</StepLabel>
                </Step>
              </Stepper>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          )}

          {/* Error Alert */}
          {submissionError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
              {submissionError}
            </Alert>
          )}

          {/* Dynamic Form */}
          <DynamicForm
            formDefinition={formDef}
            onSubmit={onSubmit}
            isProcessing={isProcessing || isSubmitting}
            submitButtonText="Antrag verschlüsselt absenden"
            processingText={getProcessingText()}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            textAlign="center"
            sx={{ mt: 2 }}
          >
            Ihre Daten werden lokal verschlüsselt und können nur von der Behörde entschlüsselt werden.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
